import {
  Box,
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  Table,
  TextField,
  Checkbox,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import ConfirmDialog from "../ConfirmDialog";
import AssignSubsDialog from "./assignSubsDialog";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

const ReportGroupSubs = ({ group }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [groupSubs, setGroupSubs] = useState([]);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Name");
  const [searchQuery, setSearchQuery] = useState("");
  const [assignSubsDialogOpen, setAssignSubsDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [subsToDelete, setSubsToDelete] = useState([]);

  const fetchSubs = async () => {
    if (!group?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers?groupId=${group.id}`
      );
      setGroupSubs(response.data || []);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
      setError("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubs();
  }, [group?.id]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedSubsData = useMemo(() => {
    if (!groupSubs) return [];

    return [...groupSubs].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Name":
          aValue = a.user.name;
          bValue = b.user.name;
          break;
        case "Email":
          aValue = a.user.email;
          bValue = b.user.email;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupSubs, order, orderBy]);

  const filteredSubsData = sortedSubsData.filter(
    (row) =>
      row.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenAssignSubsDialog = async () => {
    setAssignSubsDialogOpen(true);
  };

  const handleDeleteClick = (sub) => {
    setSubsToDelete((prevSubs) => [...prevSubs, sub.id]);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (subsToDelete.length === 0) return;
    try {
      setConfirmOpen(false);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers`,
        {
          data: { ids: subsToDelete ,UI_Description: `${getUIDescription()}\n Delete Subscriber From report group`, },
        }
      );
      if (response.status >= 200 && response.status < 300) {
        toast.success("Subscriber Successfully Deleted from Report Group");
        await fetchSubs();
        setSubsToDelete([]);
      } else {
        toast.error("Failed to delete subscriber from report group");
      }
    } catch (err) {
      console.error("❌ Error deleting subscriber from report group:", err);
      toast.error("Failed to delete subscriber from report group");
    }
  };

  const handleToggle = async (subId, field, currentValue) => {
    try {
      const body = {
        [field]: !currentValue,
        UI_Description: `${getUIDescription()}\n Update subscriber`,
      };
      await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers/${subId}`,
        body
      );
      await fetchSubs();
    } catch (err) {
      console.error("Error updating subscriber:", err);
      toast.error("Failed to update subscriber");
    }
  };

  const renderSubsTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        height: "63vh",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {["Name", "Email", "Daily", "Weekly", "Monthly", "Delete"].map(
              (col) => (
                <TableCell key={col} sx={{ fontSize: 15 }}>
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleRequestSort(col)}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSubsData.length > 0 ? (
            filteredSubsData.map((sub, index) => (
              <TableRow
                key={sub.id}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                  "&:hover": {
                    backgroundColor: "#4a4d54",
                  },
                }}
              >
                <TableCell sx={{ fontSize: 15 }}>{sub.user.name}</TableCell>
                <TableCell sx={{ fontSize: 15 }}>{sub.user.email}</TableCell>
                <TableCell sx={{ fontSize: 15 }}>
                  <Checkbox
                    checked={sub.daily}
                    onChange={() => handleToggle(sub.id, "daily", sub.daily)}
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      transform: {
                        xs: "scale(0.8)",
                        sm: "scale(0.9)",
                        md: "scale(1)",
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 15 }}>
                  <Checkbox
                    checked={sub.weekly}
                    onChange={() => handleToggle(sub.id, "weekly", sub.weekly)}
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      transform: {
                        xs: "scale(0.8)",
                        sm: "scale(0.9)",
                        md: "scale(1)",
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 15 }}>
                  <Checkbox
                    checked={sub.monthly}
                    onChange={() =>
                      handleToggle(sub.id, "monthly", sub.monthly)
                    }
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      transform: {
                        xs: "scale(0.8)",
                        sm: "scale(0.9)",
                        md: "scale(1)",
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleDeleteClick(sub)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                No subscribers found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mt: 2,
        mx: 1,
      }}
    >
      {loading ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 10,
          }}
        >
          <CircularProgress color="#60a5fa" size={60} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            py: 2,
          }}
        >
          {error}
        </Box>
      ) : (
        <>
          <Grid
            container
            spacing={{ xs: 2, md: 0 }}
            justifyContent={"space-between"}
            alignItems={"flex-end"}
            mb={2}
          >
            <Grid container size={{ xs: 12, md: 6 }}>
              <TextField
                label="Search Subscriber..."
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 200, width: 300 }}
              />
            </Grid>
            <Grid
              container
              gap={1}
              size={{ xs: 12, md: 6 }}
              justifyContent={"flex-end"}
            >
              <Button variant="contained" color="primary" onClick={fetchSubs}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenAssignSubsDialog}
              >
                Add Subscriber
              </Button>
            </Grid>
          </Grid>
          {renderSubsTable()}
        </>
      )}
      <AssignSubsDialog
        open={assignSubsDialogOpen}
        onClose={() => setAssignSubsDialogOpen(false)}
        groupId={group?.id}
        groupName={group?.name}
        fetchSubs={fetchSubs}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default ReportGroupSubs;
